import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LoginFlow from "./LoginFlow";
import Logo from "../../assets/chargingadda-logo.png";
import "./Login.scss";

const API_URL =
  process.env.REACT_APP_AUTH_URL || "https://authz-api.cms.chargingadda.com";

// Helper to get subdomain properly
const getSubdomain = (hostname: string): string => {
  const parts = hostname.split(".");
  // For "a.business.chargingadda.com" → return "a"
  // For "business.chargingadda.com" → return "" (main domain, no subdomain)
  if (parts.length > 3 || (parts.length === 3 && parts[1] === "business")) {
    return parts[0];
  }
  return "";
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  // Zitadel (Login V2) hands the browser back to us with ?authRequest=<id>;
  // then we render the in-app sign-in instead of starting a new authorize.
  const authRequestId = new URLSearchParams(window.location.search).get("authRequest");

  useEffect(() => {
    if (authRequestId) return;
    const handleAuthFlow = async () => {
      // sessionStorage survives the full-page reload that axiosInstance does on 401,
      // so this gate could trap /login and skip the Zitadel re-auth. Disabled.
      // const isRedirecting = sessionStorage.getItem("is_redirecting");
      // if (isRedirecting) {
      //   console.log("Already redirecting, skipping...");
      //   return;
      // }
      const token = localStorage.getItem("access_token");
      const currentHostname = window.location.hostname;
      const currentSubdomain = getSubdomain(currentHostname);

      if (token) {
        console.log("✅ Already logged in — redirecting to dashboard");
        // sessionStorage.setItem("is_redirecting", "true");
        navigate("/dashboard", { replace: true });
        // setTimeout(() => sessionStorage.removeItem("is_redirecting"), 1000);
        return;
      }

      // ✅ If code exists on login page, redirect to callback
      const code = new URLSearchParams(window.location.search).get("code");
      if (code) {
        console.log(
          "Auth code found on login page, redirecting to callback..."
        );
        // sessionStorage.setItem("is_redirecting", "true");
        navigate(`/callback${window.location.search}`, { replace: true });
        // setTimeout(() => sessionStorage.removeItem("is_redirecting"), 1000);
        return;
      }

      // ✅ Clear any old org data from previous sessions
      console.log("🧹 Clearing old org data to allow fresh login");
      localStorage.removeItem("user_org_subdomain");
      localStorage.removeItem("user_org_id");

      await initiateZitadelLogin(currentSubdomain);
    };

    // Duplicate invocation — handleAuthFlow() is already called at the end of this effect.
    // handleAuthFlow();

    const initiateZitadelLogin = async (subdomain: string) => {
      try {
        if (!subdomain) {
          console.error("❌ No subdomain detected! Current hostname:", window.location.hostname);
          alert("Invalid organization URL. Please use org.business.chargingadda.com");
          return;
        }

        console.log("📍 Initiating login for subdomain:", subdomain);

        // Store TEMPORARILY for callback redirect only
        localStorage.setItem("original_subdomain", subdomain);

        // Store in cookie for backend and cross-subdomain access
        document.cookie = `org_subdomain=${subdomain}; path=/; domain=.business.chargingadda.com; max-age=600; secure; SameSite=Lax`;

        // ✅ Fetch auth config from backend (backend detects subdomain from Host header)
        const configRes = await fetch(`${API_URL}/auth/config`, {
          credentials: 'include'
        });

        if (!configRes.ok) throw new Error("Failed to load auth config");
        const config = await configRes.json();

        console.log("📝 Auth config loaded for org:", config.org_name || subdomain);

        // PKCE helper functions
        const base64URLEncode = (buffer: Uint8Array) =>
          btoa(String.fromCharCode.apply(null, Array.from(buffer) as number[]))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=/g, "");

        const generateCodeVerifier = () => {
          const array = new Uint8Array(32);
          crypto.getRandomValues(array);
          return base64URLEncode(array);
        };

        const generateCodeChallenge = async (verifier: string) => {
          const data = new TextEncoder().encode(verifier);
          const hash = await crypto.subtle.digest("SHA-256", data);
          return base64URLEncode(new Uint8Array(hash));
        };

        // Generate PKCE challenge
        const verifier = generateCodeVerifier();
        const challenge = await generateCodeChallenge(verifier);
        localStorage.setItem("code_verifier", verifier);

        // Build Zitadel authorization URL
        const authUrl = new URL(`${config.authority}/oauth/v2/authorize`);
        authUrl.searchParams.append("client_id", config.client_id);
        authUrl.searchParams.append("redirect_uri", config.redirect_uri);
        authUrl.searchParams.append("response_type", "code");
        authUrl.searchParams.append("scope", config.scope);
        authUrl.searchParams.append("code_challenge", challenge);
        authUrl.searchParams.append("code_challenge_method", "S256");

        console.log("🔐 Redirecting to Zitadel...");
        window.location.href = authUrl.toString();
      } catch (err) {
        console.error("❌ Zitadel login initialization failed:", err);
      }
    };

    handleAuthFlow();
  }, [navigate, authRequestId]);

  if (authRequestId) {
    return <LoginFlow authRequestId={authRequestId} />;
  }

  return (
    <div className="login-container">
      <div className="login-splash">
        <img src={Logo} alt="ChargingAdda" />
        <div className="loader"></div>
        <p>Taking you to sign in...</p>
      </div>
    </div>
  );
};

export default Login;
